/**
 * /whatsapp/configuracoes — Singleton de ConfiguracaoWhatsapp por unidade.
 *
 * 4 campos numericos + lista tag-style de emails de alerta.
 * Gate `whatsapp:manage` (gestor).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarConfiguracao,
  listarConfiguracoes,
} from "../../../services/whatsapp";
import { PageContainer } from "../../../styles/shared";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Field,
  FieldLabel,
  Form,
  FormFooter,
  FormGrid,
  InlineHint,
  Input,
  SectionTitle,
} from "../../Orcamentos/styles";
import { Tag, TagInputWrap } from "../styles";

function ehEmailValido(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

export default function ConfiguracoesPage() {
  const { request, hasPermission } = useAuth();
  const podeGerenciar = hasPermission("whatsapp:manage");

  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [rateMsg, setRateMsg] = useState(20);
  const [rateConv, setRateConv] = useState(30);
  const [delay, setDelay] = useState(3);
  const [retencao, setRetencao] = useState(12);
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailErro, setEmailErro] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listarConfiguracoes(request);
      const lista = Array.isArray(payload) ? payload : payload?.results || [];
      const c = lista[0] || null;
      setConfig(c);
      if (c) {
        setRateMsg(c.rate_limit_msgs_minuto ?? 20);
        setRateConv(c.rate_limit_novas_conversas_hora ?? 30);
        setDelay(c.delay_entre_envios_segundos ?? 3);
        setRetencao(c.retencao_meses ?? 12);
        setEmails(Array.isArray(c.alerta_email_destinatarios) ? c.alerta_email_destinatarios : []);
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar configuração.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleAddEmail = () => {
    setEmailErro(null);
    const limpo = emailInput.trim();
    if (!limpo) return;
    if (!ehEmailValido(limpo)) {
      setEmailErro("Email inválido.");
      return;
    }
    if (emails.includes(limpo)) {
      setEmailErro("Já adicionado.");
      return;
    }
    setEmails((prev) => [...prev, limpo]);
    setEmailInput("");
  };

  const handleRemoveEmail = (email) => {
    setEmails((prev) => prev.filter((e) => e !== email));
  };

  const handleEmailKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSalvar = async () => {
    if (!config) throw new Error("Sem config carregada.");
    const dados = {
      rate_limit_msgs_minuto: Number(rateMsg) || 1,
      rate_limit_novas_conversas_hora: Number(rateConv) || 1,
      delay_entre_envios_segundos: Number(delay) || 1,
      retencao_meses: Number(retencao) || 1,
      alerta_email_destinatarios: emails,
    };
    const atualizada = await atualizarConfiguracao(request, config.id, dados);
    setConfig(atualizada);
  };

  const podeSalvar = useMemo(() => {
    if (!podeGerenciar) return false;
    if (!config) return false;
    return (
      Number(rateMsg) > 0 &&
      Number(rateConv) > 0 &&
      Number(delay) > 0 &&
      Number(retencao) > 0
    );
  }, [podeGerenciar, config, rateMsg, rateConv, delay, retencao]);

  if (loading) {
    return (
      <PageContainer>
        <EntityHeader title="Configurações" />
        <EmptyMsg>Carregando...</EmptyMsg>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <EntityHeader
        title="Configurações WhatsApp"
        subtitle="Rate limit, retenção LGPD e alertas operacionais"
      />

      {error && <ErrorMsg>{error}</ErrorMsg>}
      {!error && !config && (
        <Card>
          <EmptyMsg>
            Nenhuma configuração cadastrada para esta unidade. Crie a primeira
            instância para que a config seja criada automaticamente.
          </EmptyMsg>
        </Card>
      )}

      {config && (
        <Card>
          <SectionTitle>Parâmetros operacionais</SectionTitle>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              if (podeSalvar) handleSalvar();
            }}
          >
            <FormGrid>
              <Field>
                <FieldLabel>Limite msgs/minuto</FieldLabel>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={rateMsg}
                  onChange={(e) => setRateMsg(e.target.value)}
                  disabled={!podeGerenciar}
                />
                <InlineHint>
                  Máximo de mensagens enviadas por minuto, por instância.
                  Aumentar este valor eleva risco de ban.
                </InlineHint>
              </Field>

              <Field>
                <FieldLabel>Novas conversas/hora</FieldLabel>
                <Input
                  type="number"
                  min={1}
                  max={500}
                  value={rateConv}
                  onChange={(e) => setRateConv(e.target.value)}
                  disabled={!podeGerenciar}
                />
                <InlineHint>
                  Limite de novos contatos por hora (anti-spam).
                </InlineHint>
              </Field>

              <Field>
                <FieldLabel>Delay entre envios (s)</FieldLabel>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={delay}
                  onChange={(e) => setDelay(e.target.value)}
                  disabled={!podeGerenciar}
                />
                <InlineHint>
                  Serviço Node aplica esse delay (varia entre delay e delay+2s)
                  pra simular padrão humano.
                </InlineHint>
              </Field>

              <Field>
                <FieldLabel>Retenção (meses) — LGPD</FieldLabel>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={retencao}
                  onChange={(e) => setRetencao(e.target.value)}
                  disabled={!podeGerenciar}
                />
                <InlineHint>
                  Após esse período, mensagens podem ser anonimizadas via
                  command{" "}
                  <code>lgpd_anonimizar_conversa --antigas-meses=N</code>.
                </InlineHint>
              </Field>
            </FormGrid>

            <Field style={{ marginTop: 16 }}>
              <FieldLabel>Alertas por email</FieldLabel>
              <TagInputWrap>
                {emails.map((e) => (
                  <Tag key={e}>
                    {e}
                    {podeGerenciar && (
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(e)}
                        title="Remover"
                      >
                        <FiX size={12} />
                      </button>
                    )}
                  </Tag>
                ))}
                {podeGerenciar && (
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(ev) => setEmailInput(ev.target.value)}
                    onKeyDown={handleEmailKey}
                    onBlur={handleAddEmail}
                    placeholder={emails.length === 0 ? "email@empresa.com" : ""}
                  />
                )}
              </TagInputWrap>
              {emailErro && <ErrorMsg style={{ marginTop: 6 }}>{emailErro}</ErrorMsg>}
              <InlineHint>
                Enter ou vírgula para adicionar. Destinatários recebem alerta
                quando instância desconecta inesperadamente ou rate-limit é
                atingido.
              </InlineHint>
            </Field>

            {podeGerenciar && (
              <FormFooter>
                <AsyncButton
                  variant="primary"
                  onClick={handleSalvar}
                  disabled={!podeSalvar}
                  successMessage="Configurações salvas"
                  errorMessage="Falha ao salvar"
                >
                  Salvar
                </AsyncButton>
              </FormFooter>
            )}
            {!podeGerenciar && (
              <InlineHint>
                Apenas usuários com permissão "whatsapp:manage" podem editar.
              </InlineHint>
            )}
          </Form>
        </Card>
      )}
    </PageContainer>
  );
}
